import { mutateIncomingInspection } from "@/api/incomingInspection/useIncomingInspection";
import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { items } from "@/api/items/useItemQuery";
import { ItemAutoComplete } from "@/custom/features/ui/autoComplete/item/item-autoComplete";
import SubBagInputGrid from "@/features/inventory/components/form/inspection/SubBagInputGrid";
import SubInspectionTable from "@/features/inventory/components/form/inspection/SubInspectionTable";
import { LotCreateForm } from "@/features/lot/form/lotCreateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { Calendar, isCalendarDate } from "@/features/ui/calendar";
import { setDateFormatString } from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex, NumberInput, Table, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  DefaultApiWmsIncomingPostRequest,
  IncomingInspectionsGetRequest,
  SpcApiIncomingInspectionsPostRequest,
  WorkLogsWorkLogIdCancelStartPut200ResponseCancelConsumeInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendarEvent } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export interface IncomingInspectionFormValues
  extends IncomingInspectionsGetRequest,
    Partial<Pick<WorkLogsWorkLogIdCancelStartPut200ResponseCancelConsumeInner, "locationCode">> {
  partnerName?: string;
  actualDate?: string; // ISO string
}

const IncomingInspection = Object.assign({}, FormMain, {
  Wrapper: FormWrapper,
  ItemAutoComplete: ItemAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  LotCreateForm: LotCreateForm,
  Table: SubInspectionTable,
  Input: TextInput,
  NumberInput: NumberInput,
  Button: Button,
  SubBagInputGrid: SubBagInputGrid,
});

const BAG_NUM = 40;

export const IncomingInspectionForm = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();

  const form = useForm<IncomingInspectionFormValues>({
    initialValues: {
      partnerCode: "",
      partnerName: undefined,
      details: [],
      itemCode: "",
      locationCode: "",
      lotId: undefined,
      lotInfo: {
        name: undefined,
        expiration: undefined,
      },
      inspectionAt: new Date().toISOString(),
      inspectionResults: [],
      defectReason: "",
      actualDate: new Date().toISOString(),
    },
    validate: {
      itemCode: (value) => (value ? null : "품목을 선택해주세요."),
      locationCode: (value) => (value ? null : "로케이션코드를 선택해주세요."),
      lotId: (value) => (value ? null : "로트번호를 입력해주세요."),
      partnerCode: (value) => (value ? null : "거래처를 선택해주세요."),
    },
  });

  const { data: itemData } = useQuery({
    ...items.getView({
      query: form.values.itemCode ? { $and: [{ code: { $eq: form.values.itemCode } }] } : "",
    }),
    enabled: !!form.values.itemCode,
  });

  const { mutate: incomingMutate } = useMutation(
    (params: DefaultApiWmsIncomingPostRequest) =>
      mutateInventories.incoming(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        customAlert("재고 입고에 성공하였습니다.", "재고 입고 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "재고 입고" }),
          "재고 입고 실패",
          "red",
        );
      },
    },
  );

  const { mutateAsync: inspectionMutate } = useMutation(
    (params: SpcApiIncomingInspectionsPostRequest) =>
      mutateIncomingInspection.create(params).mutationFn(undefined),
    {
      onSuccess: () => {
        customAlert("수입검사 등록에 성공하셨습니다.", "수입검사 등록 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "수입 검사" }),
          "수입 검사 실패",
          "red",
        );
      },
    },
  );

  const item = itemData?.data?.rows?.[0];
  const defectBagCount = form.values.details?.filter((bag) => bag.isReturned).length;
  const goodQuantity = form.values.details?.length
    ? form.values.details?.reduce(
        (sum, bag) => (!bag.isReturned ? Number(sum) + Number(bag.quantity ?? 0) : Number(sum)),
        0,
      )
    : 0;
  const defectQuantity = form.values.details?.reduce(
    (sum, bag) => (bag.isReturned ? Number(sum) + Number(bag.quantity ?? 0) : Number(sum)),
    0,
  );

  const goodBagCount = form.values.details?.filter((bag) => !bag.isReturned && bag.quantity).length;
  const isIncomingDisabled = !form.values.locationCode?.trim();

  const openIncomingDateCalendar = () => {
    openModal(<Calendar type="default" dates={[startDate, endDate]} />, null, "날짜 선택", true).then(
      (value) => {
        if (isCalendarDate(value)) {
          setStartDate(value[0]);
          setEndDate(value[1]);
          form.setFieldValue("inspectionAt", value[0]?.toISOString());
          form.setFieldValue("actualDate", value[0]?.toISOString());
        }
      },
    );
  };

  const handleSubmit = async () => {
    if (!form.values.itemCode || !form.values.locationCode) {
      return;
    }
    const params: DefaultApiWmsIncomingPostRequest = {
      workLogsWorkLogIdCancelStartPut200ResponseCancelConsumeInner: {
        itemCode: form.values.itemCode,
        locationCode: form.values.locationCode,
        quantity: String(goodQuantity ?? 0),
        lotId: Number(form.values.lotId),
        lotInfo: {
          name: form.values.itemCode && form.values.lotInfo?.name,
          expiration:
            form.values.itemCode && form.values.lotInfo?.expiration === "null"
              ? null
              : form.values.lotInfo?.expiration,
        },
        actionFrom: "WEB",
        actualDate: form.values.actualDate,
      },
    };

    const inspectionParams: SpcApiIncomingInspectionsPostRequest = {
      incomingInspectionsGetRequest: {
        partnerCode: form.values.partnerCode,
        itemCode: form.values.itemCode,
        lotId: Number(form.values.lotId),
        lotInfo: {
          name: form.values.itemCode && form.values.lotInfo?.name,
          expiration:
            form.values.itemCode && form.values.lotInfo?.expiration === "null"
              ? null
              : form.values.lotInfo?.expiration,
        },
        inspectionAt: form.values.inspectionAt as string,
        goodQuantity: String(goodQuantity ?? 0),
        defectQuantity: String(defectQuantity ?? 0),
        goodBagCount: String(goodBagCount ?? 0),
        defectBagCount: String(defectBagCount ?? 0),
        defectReason: form.values.defectReason,
        inspectionResults: form.values.inspectionResults,
        details: form?.values?.details?.map((bag, index) => ({
          seq: index,
          quantity: bag.quantity,
          isReturned: bag.isReturned,
        })),
      },
    };

    const result = await inspectionMutate(inspectionParams);

    if (result.data?.id) {
      params.workLogsWorkLogIdCancelStartPut200ResponseCancelConsumeInner.incomingInspectionId =
        result.data.id;
      await incomingMutate(params);
      closeModal({});
    }
  };

  useEffect(() => {
    form.setFieldValue("details", [
      ...Array.from({ length: BAG_NUM }, (_, i) => ({
        seq: i,
        quantity: undefined,
        isReturned: undefined,
      })),
    ]);
  }, []);

  return (
    <IncomingInspection.Wrapper onSubmit={form.onSubmit(handleSubmit)} miw={1300} maw={1600}>
      <Flex gap={"xl"}>
        <div>
          <Title underline align="center" mb={"xl"}>
            {"입고 / 수입 검사 내역서"}
          </Title>
          <Flex fw={"bold"} mb={"xl"} align={"center"} justify={"space-between"}>
            <Flex align={"center"}>
              <Text pr={"xs"} w={"5rem"}>
                거래처 :{" "}
              </Text>
              <PartnerAutoComplete
                placeholder="거래처 선택"
                {...form.getInputProps(`partnerCode`)}
                setName={(name: string) => form.setFieldValue("partnerName", name)}
                partnerType={["PURCHASE", "BI_DIRECTION"]}
              />
            </Flex>
            <Flex gap={"xl"}>
              <Text
                role="button"
                tabIndex={0}
                title="입고일자 수정"
                onClick={openIncomingDateCalendar}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openIncomingDateCalendar();
                  }
                }}
                style={{
                  alignItems: "center",
                  color: "#1c7ed6",
                  cursor: "pointer",
                  display: "inline-flex",
                  gap: 4,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                <IconCalendarEvent size={16} />
                입고 일자 : {setDateFormatString(startDate ?? undefined)}
              </Text>
              <Text>
                로트No. : {form.values.lotInfo?.name ?? form.values.lotInfo?.expiration ?? "-"}
              </Text>
            </Flex>
          </Flex>
          <Table withBorder withColumnBorders highlightOnHover>
            <thead>
              <tr>
                <th style={{ textAlign: "center", fontWeight: "bold" }}>{"품명"}</th>
                <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Grade"}</th>
                <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Color"}</th>
                <th style={{ textAlign: "center", fontWeight: "bold" }}>{"형태"}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ textAlign: "center" }}>{item?.name ?? "-"}</td>
                <td style={{ textAlign: "center" }}>{item?.gradeName ?? "-"}</td>
                <td style={{ textAlign: "center" }}>{item?.itemCategoryName ?? "-"}</td>
                <td style={{ textAlign: "center" }}>{item?.abstractItemCode ?? "-"}</td>
              </tr>
            </tbody>
          </Table>
          <IncomingInspection.Table
            title="수입검사"
            columns={["RoHS 확인", "재질(연소)", "포장상태", "수분상태"]}
            form={form}
          />
          <Table withBorder withColumnBorders highlightOnHover>
            <tbody>
              <tr>
                <td>
                  <Text align="center" fw={"bold"}>
                    {"양품수량"}
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    <IncomingInspection.Input value={goodQuantity ?? 0} />
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    {"반품수량"}
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    <IncomingInspection.Input value={defectQuantity ?? 0} />
                  </Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Text align="center" fw={"bold"}>
                    {"양품 Bag수"}
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    <IncomingInspection.Input value={goodBagCount ?? 0} />
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    {"반품 Bag수"}
                  </Text>
                </td>
                <td>
                  <Text align="center" fw={"bold"}>
                    <IncomingInspection.Input value={defectBagCount ?? 0} />
                  </Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Text align="center" fw={"bold"}>
                    {"반품 사유"}
                  </Text>
                </td>
                <td colSpan={3}>
                  <IncomingInspection.Input
                    placeholder="반품 사유를 입력해주세요"
                    {...form.getInputProps("defectReason")}
                  />
                </td>
              </tr>
            </tbody>
          </Table>
          <IncomingInspection.SubBagInputGrid bagNum={BAG_NUM} form={form} />
        </div>
        <Flex gap={"xl"} direction={"column"}>
          <IncomingInspection.ItemAutoComplete
            label="품목"
            withAsterisk
            {...form.getInputProps("itemCode")}
          />
          <IncomingInspection.LocationAutoComplete
            label="로케이션코드"
            withAsterisk
            {...form.getInputProps("locationCode")}
          />
          <IncomingInspection.LotCreateForm itemCode={item?.code ?? "0"} form={form} />
        </Flex>
      </Flex>
      <Flex gap={"xs"} justify={"flex-end"}>
        <IncomingInspection.Button color="gray" onClick={() => closeModal({})}>
          {"취소"}
        </IncomingInspection.Button>
        <IncomingInspection.Button type="submit" disabled={isIncomingDisabled}>
          {"입고"}
        </IncomingInspection.Button>
      </Flex>
    </IncomingInspection.Wrapper>
  );
};

export default IncomingInspectionForm;
